import { useCallback } from 'react';
import styled from 'styled-components';
import { AppAudioPlayerPlayerMinimize } from '@elfsight/icons';
import { getColorWithAlpha } from '@elfsight/widget-utils';
import { useTheme } from '../../../common';
import { useFloating } from '../../layouts/floating';
import type { DefaultButtonProps } from './DefaultButton';
import { DefaultButton } from './DefaultButton';

interface MinimizeButtonProps extends DefaultButtonProps {
  revert?: boolean;
  floating?: boolean;
  size?: number;
  iconSize?: number;
  styles?: string;
}

export function MinimizeButton({
  styles,
  revert = false,
  floating = false,
  size = 32,
  iconSize = 24,
  ...forwardingProps
}: MinimizeButtonProps) {
  const { setMinimize, minimizePlayer } = useFloating();
  const { textColor } = useTheme();

  const switchMinimization = useCallback(() => {
    setMinimize(!minimizePlayer);
  }, [minimizePlayer, setMinimize]);

  return (
    <StyledButton
      {...forwardingProps}
      onClick={switchMinimization}
      _floating={floating}
      _size={size}
      _styles={styles}
    >
      <StyledIcon
        as={AppAudioPlayerPlayerMinimize}
        _revert={revert}
        _fill={getColorWithAlpha(textColor, 1)}
        _size={iconSize}
      />
    </StyledButton>
  );
}

const StyledButton = styled(DefaultButton)<{
  _size: number;
  _floating: boolean;
}>`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ _size }) => _size}px;
  height: ${({ _size }) => _size}px;
  ${({ _styles }) => _styles};
  ${({ _floating }) =>
    _floating &&
    `
      position: absolute;
      top: 0;
      right: 0;
      z-index: 999;
    `}
`;

const StyledIcon = styled.svg<{
  _revert: boolean;
  _fill: string;
  _size: number;
}>`
  fill: ${({ _fill }) => _fill};
  width: ${({ _size }) => _size}px;
  height: ${({ _size }) => _size}px;

  ${({ _revert }) => _revert && `transform: rotate(180deg);`};
`;
